import { BiX } from "react-icons/bi";
import { BsFilter } from "react-icons/bs";
import { withTranslation } from "react-i18next";
import ReportingPeriod from "../Regulation/ReportingPeriod";
import { Button, Form } from "react-bootstrap";
import { useContext, useEffect, useState } from "react";
import Select, { components } from "react-select";
import { currentYear, DataViewCategoryType, MaterialCategoryList, StandardCategory, startYear } from "../../core/constants/common";
import StandardService from "../../core/service/StandardService";
import { RegulationContext } from "../../App";
import { getCompanyInfoFromStorage, getDefaultSelectableYear } from "../../core/utilities/assessmentPeriodUtils";

// Same react-select building blocks/skin ("dvSelect") as DataViewFilterNew.js,
// kept local here the same way DataViewFilterNew.js keeps them local.
const MAX_VISIBLE_CHIPS = 2;

const flattenSelectOptions = (options) => {
	return (options ?? []).reduce((acc, item) => {
		if (item?.options) {
			return acc.concat(flattenSelectOptions(item.options));
		}
		acc.push(item);
		return acc;
	}, []);
};

const MultiSelectOption = (props) => {
	const { isSelected, label } = props;
	return (
		<components.Option {...props}>
			<Form.Check
				type="checkbox"
				readOnly
				checked={isSelected}
				label={label}
				className="dvSelect-option-check" />
		</components.Option>
	);
};

const MultiValueChip = (props) => {
	const { index, getValue } = props;
	if (index < MAX_VISIBLE_CHIPS) {
		return <components.MultiValue {...props} />;
	}
	if (index === MAX_VISIBLE_CHIPS) {
		return (
			<div className="dvSelect__multi-value dvSelect__multi-value--overflow">
				<span className="dvSelect__multi-value__label">{props.selectProps.moreLabel(getValue().length - MAX_VISIBLE_CHIPS)}</span>
			</div>
		);
	}
	return null;
};

const MultiSelectMenuList = (props) => {
	const { children, options, selectProps } = props;
	return (
		<components.MenuList {...props}>
			<div className="dvSelect__menu-actions">
				<button type="button"
					className="dvSelect__menu-action"
					onMouseDown={(e) => {
						e.preventDefault();
						const flatOptions = flattenSelectOptions(options);
						const limitedOptions = selectProps.selectionLimit
							? flatOptions.slice(0, selectProps.selectionLimit)
							: flatOptions;
						selectProps.onChange(limitedOptions, { action: 'select-option' });
					}}>
					{selectProps.selectAllLabel}
				</button>
				<button type="button"
					className="dvSelect__menu-action dvSelect__menu-action--clear"
					onMouseDown={(e) => { e.preventDefault(); selectProps.onChange([], { action: 'clear' }); }}>
					{selectProps.clearAllLabel}
				</button>
			</div>
			{children}
		</components.MenuList>
	);
};

const AppFilter = ({ t, type, existingFilter, onFilterHide, onApplyFilter, selectedTab, esgFilter }) => {

	const { glbRegulation, setGlbRegulation } = useContext(RegulationContext);
	const glbRegulationId = glbRegulation?.id ? glbRegulation.id : "";

	const defaultEndYear = getDefaultSelectableYear(getCompanyInfoFromStorage());

	const [topics, setTopics] = useState([]);
	const [filter, setFilter] = useState({
		period: existingFilter?.period ?? esgFilter?.reportingPeriod ?? `${defaultEndYear}-01-01`,
		category: 'Environmental',
		annualyear: new Date().getFullYear(),
		selectedTopic: null,
		categoryType: esgFilter?.categoryType ?? DataViewCategoryType.ENVIRONMENTAL,
		topicsFromFilter: null,
		selectedTopicIds: esgFilter?.topicIds ?? [],
	});

	const yearOptions = [];
	for (let i = currentYear; i >= startYear; i--) {
		yearOptions.push(<option value={i} key={i}>{i}</option>);
	}

	useEffect(() => {
		if (!glbRegulationId) return;

		if (type === 'DASHBOARD') {
			getTopics(filter.category, filter.annualyear, true);
		} else if (type === 'NR_ESG') {
			getQualitativeTopics(filter.categoryType, filter.period, filter.selectedTopicIds, true);
		}
	}, [selectedTab, glbRegulationId]);

	const getTopics = (category, year, doApplyFilter = false) => {
		const cat = category === 'Social'
			? DataViewCategoryType.SOCIAL : category === 'Governance'
				? DataViewCategoryType.GOVERNANCE : DataViewCategoryType.ENVIRONMENTAL;
		StandardService.getTopicsByStandard(glbRegulationId, false, StandardCategory.QUANTITATIVE, cat)
			.then(res => {
				if (res.status !== 200) return;

				setTopics(res.data);
				if (doApplyFilter) {
					const t = res.data[0] ? { id: res.data[0].id, name: res.data[0].name } : null;
					const f = { ...filter, selectedTopic: null, category: category, annualyear: year }
					setFilter(f);
					onApplyFilter(f);
				}
			}).catch(err => { });
	};

	const getQualitativeTopics = (category, period, tIds, doApplyFilter = false) => {
		StandardService.getTopicsByCategoryAndStandard(
			category,
			glbRegulation.id, period ?? filter.reportingPeriod,
			StandardCategory.QUALITATIVE
		).then(res => {
			if (res.status !== 200) return;

			setTopics(res.data.sort((a, b) => a.sortOrder - b.sortOrder));
			const t = res.data;
			const f = { ...filter, topicsFromFilter: t, categoryType: category, period: period, selectedTopicIds: tIds }
			setFilter(f);
			// Always call onApplyFilter to ensure parent component gets updated with topics and selectedTopicIds
			onApplyFilter(f);
		}).catch(err => { });
	};

	const handleCategoryChange = (val) => {
		const updatedFilter = { ...filter, category: val };
		setFilter(updatedFilter);
		getTopics(updatedFilter.category);
	}

	const handleTopicChange = (val) => {
		const t = topics?.find(t => t.id === +val);
		setFilter({ ...filter, selectedTopic: t });
	}

	const handleCategoryTypeChange = (val) => {
		const updatedFilter = { ...filter, categoryType: val };
		setFilter(updatedFilter);
		getQualitativeTopics(updatedFilter.categoryType, filter.period, filter.selectedTopicIds);
	}

	const onTopicsChange = (selectedItems) => {
		setFilter({
			...filter,
			selectedTopicIds: (selectedItems ?? []).map(item => item.id),
		});
	};

	const getSelectedTopics = () => {
		const data = [];
		topics?.forEach(topic => {
			if (filter.selectedTopicIds.indexOf(topic.id) < 0) return;

			data.push(topic);
		});
		return data;
	};

	const renderReportingPeriod = () => {
		return (
			<div className="col-lg-4 p-2">
				<label>{t("Year.label")}</label>
				<ReportingPeriod
					useFyYearLabels
					defaultFrequency='ANNUAL'
					defaultPeriod={filter.period}
					valueChangeCallback={(p) => setFilter({ ...filter, period: `${p.year}-01-01` })} />
			</div>
		)
	}

	const renderGeneralDisclosureFilters = () => {
		return (
			<>
				{renderReportingPeriod()}
			</>
		)
	}

	const renderDashboardFilters = () => {
		return (
			<>
				<div className="col-lg-4 p-2">
					<label className="mb-1">{t('Category.label')}</label>
					<select className="form-select" value={filter.category} onChange={(e) => handleCategoryChange(e.target.value)}>
						<option value="Environmental" key="Environmental">{t("Environmental.label")}</option>
						<option value="Social" key="Social">{t("Social.label")}</option>
						<option value="Governance" key="Governance">{t("Governance.label")}</option>
					</select>
				</div>

				<div className="col-lg-4 p-2">
					<label className="mb-1">{t('Year.label')}</label>
					<select className="form-select" value={filter.annualyear} onChange={(e) => setFilter({ ...filter, annualyear: e.target.value })}>
						{yearOptions}
					</select>
				</div>

				{/* <div className="col-lg-4 p-2">
					<label className="mb-1">{t('Topic.label')}</label>
					<select className="form-select"
						value={filter.selectedTopic?.id}
						onChange={(e) => handleTopicChange(e.target.value)}>
						{
							topics?.map((topic, index) => {
								return <option value={topic.id} key={`topic-${index}`}>{topic.name}</option>;
							})
						}
					</select>
				</div> */}
			</>
		)
	}

	const renderContentBlockFilters = () => {
		return (
			<>
				{renderReportingPeriod()}
			</>
		)
	}

	const renderESGFilters = () => {
		return (
			<>
				{renderReportingPeriod()}

				<div className="col-lg-4 p-2">
					<label htmlFor="esgFilter" >{t("Category.label")}</label>
					<select id="esgFilter"
						className="form-select"
						onChange={(e) => handleCategoryTypeChange(e.target.value)}
						required
						value={filter.categoryType}>
						{
							MaterialCategoryList({ t }).map(c => {
								return <option value={c.id} key={c.key}>{c.text}</option>
							})
						}
					</select>
				</div>

				{filter.categoryType != 3 && (
					<div className="col-lg-4 p-2">
						<label>{t("Topic.label")}</label>
						<Select
							classNamePrefix="dvSelect"
							unstyled
							isMulti
							inputId="topicMultiSelect"
							options={topics}
							value={getSelectedTopics()}
							getOptionValue={(o) => o.id}
							getOptionLabel={(o) => o.name}
							onChange={onTopicsChange}
							placeholder={t("Select.label")}
							noOptionsMessage={() => t("NoData.label")}
							selectAllLabel={t("SelectAll.label")}
							clearAllLabel={t("ClearAll.label")}
							moreLabel={(count) => t("MoreSelected.label", { count })}
							components={{ Option: MultiSelectOption, MultiValue: MultiValueChip, MenuList: MultiSelectMenuList }}
							hideSelectedOptions={false}
							closeMenuOnSelect={false}
							menuPosition="fixed" />
					</div>
				)}
			</>
		)
	}

	const applyFilter = () => {
		onApplyFilter(filter);
		onFilterHide(false);
	}

	const resetFilter = () => {
		const resetEndYear = getDefaultSelectableYear(getCompanyInfoFromStorage());
		const resetPeriod = `${resetEndYear}-01-01`;

		if (type === 'GD') {
			setFilter({ period: resetPeriod });
			const filterToSend = { period: resetPeriod };
			onApplyFilter(filterToSend);
		}

		if (type === 'DASHBOARD') {
			setFilter({
				category: 'Environmental',
				annualyear: new Date().getFullYear(),
			});
			getTopics('Environmental', new Date().getFullYear(), true);
		}

		if (type === 'NR_CONTENTBLOCKS') {
			setFilter({ period: resetPeriod });
			const filterToSend = { period: resetPeriod };
			onApplyFilter(filterToSend);
		}

		if (type === 'NR_ESG') {
			setFilter({
				period: resetPeriod,
				categoryType: DataViewCategoryType.ENVIRONMENTAL,
				selectedTopicIds: []
			});
			getQualitativeTopics(DataViewCategoryType.ENVIRONMENTAL, resetPeriod, [], true);
		}
	}

	return (
		<div>
			{
				<div className="dv-filter-holder 6">
					<div className="dv-filter-holder_head">
						<h5><BsFilter className="dv-filter-holder_headIcon" />{t("Filter.label")}</h5>
						<div
							className="btnClose"
							onClick={() => onFilterHide(false)}>
							<BiX />
						</div>
					</div>
					<div className="dv-filter-holder_body">
						<div className="row m-0">
							{
								(type === 'GD') &&
								renderGeneralDisclosureFilters()
							}
							{
								(type === 'DASHBOARD') &&
								renderDashboardFilters()
							}
							{
								(type === 'NR_CONTENTBLOCKS') &&
								renderContentBlockFilters()
							}
							{
								(type === 'NR_ESG') &&
								renderESGFilters()
							}
						</div>
					</div>
					<div className="dv-filter-holder_footer">
						<Button variant="outline-secondary"
							onClick={() => resetFilter()}
							className="px-4">
							{t("Reset.label")}
						</Button>
						<Button variant="primary"
							onClick={() => applyFilter()}
							className="sbmBtn px-4">
							{t("Apply.label")}
						</Button>
					</div>
				</div>
			}
		</div>
	);

};

export default withTranslation()(AppFilter);